import { PaperCatalogRecord, usePaperCatalogCache } from "api/cached";
import { OptionItem } from "commons/ultilities/dynamic-form/models/control";
import { mapToDistinctOptionItems } from "commons/ultilities/option-item/option-item-ultil";
import { useCallback, useEffect, useMemo } from "react";

export type PaperMaterialFilterKey =
  | "code"
  | "specificationSize"
  | "brand"
  | "rollNumber"
  | "type"
  | "gsm";

export type PaperMaterialFilterSelection = {
  code: string;
  specificationSize: string;
  brand: string;
  rollNumber: string;
  type: string;
  gsm: string;
};

const FILTER_KEYS: PaperMaterialFilterKey[] = [
  "code",
  "specificationSize",
  "brand",
  "rollNumber",
  "type",
  "gsm",
];

const normalizeValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const filterByKeyword = (options: OptionItem[], keyword: string) => {
  if (!keyword) return options;
  const normalizedKeyword = keyword.toLowerCase();
  return options.filter((x) =>
    x.label.toLowerCase().includes(normalizedKeyword),
  );
};

const applySelection = (
  records: PaperMaterialFilterSelection[],
  selection: PaperMaterialFilterSelection,
  ignoreKey?: PaperMaterialFilterKey,
) => {
  return records.filter((record) => {
    return FILTER_KEYS.every((key) => {
      if (key === ignoreKey) return true;
      const selectedValue = selection[key];
      if (!selectedValue) return true;
      return record[key] === selectedValue;
    });
  });
};

const getOptionsByKey = (
  records: PaperMaterialFilterSelection[],
  key: PaperMaterialFilterKey,
): OptionItem[] => {
  return mapToDistinctOptionItems(
    records.filter((record) => !!record[key]),
    (record) => record[key],
  ).sort((a, b) => a.label.localeCompare(b.label));
};

export const usePaperMaterialFilterSelection = (
  selection: PaperMaterialFilterSelection,
) => {
  const { data, error } = usePaperCatalogCache();

  const records = useMemo<PaperMaterialFilterSelection[]>(() => {
    return (
      data?.map(
        (record: PaperCatalogRecord): PaperMaterialFilterSelection => ({
          code: normalizeValue(record.code),
          specificationSize: normalizeValue(record.specificationSize),
          brand: normalizeValue(record.brand),
          rollNumber: normalizeValue(record.rollNumber),
          type: normalizeValue(record.type),
          gsm: normalizeValue(record.gsm),
        }),
      ) ?? []
    );
  }, [data]);

  useEffect(() => {
    if (!error) return;
    console.error("Fail load paper filter data", error);
  }, [error]);

  const searchOptions = useCallback(
    async (key: PaperMaterialFilterKey, keyword: string) => {
      const filteredRecords = applySelection(records, selection, key);
      const options = getOptionsByKey(filteredRecords, key);
      return filterByKeyword(options, keyword);
    },
    [records, selection],
  );

  const searchCodeOptions = useCallback(
    async (keyword: string) => searchOptions("code", keyword),
    [searchOptions],
  );

  const searchSpecificationSizeOptions = useCallback(
    async (keyword: string) => searchOptions("specificationSize", keyword),
    [searchOptions],
  );

  const searchBrandOptions = useCallback(
    async (keyword: string) => searchOptions("brand", keyword),
    [searchOptions],
  );

  const searchRollNumberOptions = useCallback(
    async (keyword: string) => searchOptions("rollNumber", keyword),
    [searchOptions],
  );

  const searchTypeOptions = useCallback(
    async (keyword: string) => searchOptions("type", keyword),
    [searchOptions],
  );

  const searchGsmOptions = useCallback(
    async (keyword: string) => searchOptions("gsm", keyword),
    [searchOptions],
  );

  const getValidSelectionAfterChange = useCallback(
    (
      keyChanged: PaperMaterialFilterKey,
      valueChanged: string,
    ): PaperMaterialFilterSelection => {
      const nextSelection: PaperMaterialFilterSelection = {
        ...selection,
        [keyChanged]: valueChanged,
      };

      for (const key of FILTER_KEYS) {
        if (key === keyChanged || !nextSelection[key]) continue;

        const filteredRecords = applySelection(records, nextSelection, key);
        const optionValues = new Set(
          getOptionsByKey(filteredRecords, key).map((x) => x.value),
        );

        if (!optionValues.has(nextSelection[key])) {
          nextSelection[key] = "";
        }
      }

      return nextSelection;
    },
    [records, selection],
  );

  return useMemo(
    () => ({
      searchCodeOptions,
      searchSpecificationSizeOptions,
      searchBrandOptions,
      searchRollNumberOptions,
      searchTypeOptions,
      searchGsmOptions,
      getValidSelectionAfterChange,
    }),
    [
      getValidSelectionAfterChange,
      searchBrandOptions,
      searchCodeOptions,
      searchGsmOptions,
      searchRollNumberOptions,
      searchSpecificationSizeOptions,
      searchTypeOptions,
    ],
  );
};
